import React from 'react';
import { graphql } from 'gatsby';
import { brandColours } from '../styles';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';
import SubBanner from '../components/SubBanner';
import EventDetail from '../components/EventDetail';
import FeaturedProducts from '../components/FeaturedProducts';
import UpcomingEvents from '../components/UpcomingEvents';
import { orderFeaturedProducts } from '../utils';

const DefaultPageTemplate = ({
  data: {
    datoCmsEvent: {
      seoMetaTags,
      logo,
      categories,
      content,
      startDate,
      startDateFormatted,
      startDateTime,
      endDate,
      endDateFormatted,
      endDateTime,
      website,
      venue,
      title,
    },
    datoCmsEventSitewide: { ctaHeading, ctaLink },
    datoCmsEventsArchive: { bannerImage },
    allShopifyProduct: { nodes: featuredProducts },
    allDatoCmsEvent: { nodes },
  },
  pageContext: { featuredProducts: featuredProductsOrder },
}) => {
  const updateSeoTitleTag = seoMetaTags => {
    const i = seoMetaTags.tags.findIndex(({ tagName }) => tagName === 'title');
    const title = seoMetaTags.tags[i].content;
    const suffix = ' | UK Exhibition Calendar';

    if (!title.endsWith(suffix)) {
      seoMetaTags.tags[i].content = title + suffix;
    }

    return seoMetaTags;
  };

  return (
    <Layout seo={updateSeoTitleTag(seoMetaTags)}>
      <main>
        <Breadcrumbs
          currentPageTitle={title}
          items={[
            {
              text: 'Exhibition Calendar',
              urlPath: '/exhibition-calendar/',
            },
          ]}
        />
        <SubBanner
          heading={title}
          image={logo}
          dates={{ startDate, startDateFormatted, endDate, endDateFormatted }}
          venue={venue}
          backgroundImage={bannerImage}
          backgroundColour={{ hex: brandColours.primary }}
        />
        <EventDetail
          content={content}
          venue={venue}
          cta={{ heading: ctaHeading, link: ctaLink }}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          categories={categories}
          website={website}
        />
        <FeaturedProducts
          heading="Popular products for this exhibition"
          items={orderFeaturedProducts(featuredProducts, featuredProductsOrder)}
          alt={true}
        />
        <UpcomingEvents
          heading="More Upcoming Trade Shows"
          items={nodes}
          alt={true}
        />
      </main>
    </Layout>
  );
};

export const EventDetailPageTemplateQuery = graphql`
  query EventDetailPageTemplateQuery(
    $id: String!
    $todaysDate: Date!
    $featuredProducts: [String!]
  ) {
    datoCmsEvent(id: { eq: $id }) {
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      logo {
        ...SubBannerImageFragment
      }
      categories {
        title
      }
      content {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
      slug
      startDate
      startDateFormatted: startDate(formatString: "Do MMM YYYY")
      startDateTime: startDate(formatString: "DD MMMM YYYY @ HH:mm")
      endDate
      endDateFormatted: endDate(formatString: "Do MMM YYYY")
      endDateTime: endDate(formatString: "DD MMMM YYYY @ HH:mm")
      website
      title
      venue {
        address
        name
        phoneNumber
        region
        venueWebsite: website
        ...LinkFragment
      }
    }
    datoCmsEventSitewide {
      ctaHeading
      ctaLink {
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    datoCmsEventsArchive {
      bannerImage {
        gatsbyImageData(
          width: 2000
          height: 430
          layout: FULL_WIDTH
          imgixParams: { w: "2000", h: "430" }
        )
        alt
      }
    }
    allShopifyProduct(filter: { handle: { in: $featuredProducts } }) {
      nodes {
        ...ProductCardFragment
      }
    }
    allDatoCmsEvent(
      filter: { startDate: { gte: $todaysDate }, id: { ne: $id } }
      limit: 8
      sort: { startDate: ASC }
    ) {
      nodes {
        ...EventCardFragment
      }
    }
  }
`;

export default DefaultPageTemplate;
